import { mat3, mat4, quat, vec3, vec4 } from 'gl-matrix';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { ProjectionMode } from '@kitware/vtk.js/Rendering/Core/ImageCPRMapper/Constants'; // enum
import vtkImageCPRMapper from '@kitware/vtk.js/Rendering/Core/ImageCPRMapper';
import {
  degreesFromRadians,
  radiansFromDegrees,
} from '@kitware/vtk.js/Common/Core/Math';
import { createCPRActorAndMapper, rotateView } from '../vtkUtilites';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import {
  captureCPRPNG,
  captureView,
  setNewContainer,
  trimMarginsToContent,
} from 'utils/captureUtils';
import vtkGenericRenderWindow from '@kitware/vtk.js/Rendering/Misc/GenericRenderWindow';
import { VOI } from 'config/analysisConfig';

type Vec3 = [number, number, number];

export interface CPRExportOptions {
  outWidthPx?: number;
  outHeightPx?: number;
  pixelSpacingU?: number; // mm/px (default 0.5)
  pixelSpacingV?: number; // mm/px (default 0.5)
  projectionAxis?: 'normal' | 'bitangent' | 'tangent';
  inPlaneAngleRad?: number; // 고정 라디안 각
  rotationAxisWorld?: vec3;
  angle?: number;
}

export interface CPRExportResult {
  width: number;
  height: number;
  spacingU: number;
  spacingV: number;
  data: Int16Array; // HU
  extent: { minHU: number; maxHU: number; nanCount: number };
  angle: number;
}

export interface CPRLabelExportResult {
  width: number;
  height: number;
  data: Uint8Array;
  widthMM: number;
  heightMM: number;
  rows: Array<{
    center: Vec3;
    direction: Vec3;
    slabAxis: Vec3;
  }>;
}

function applyMat4Point(m: mat4, p: Vec3): Vec3 {
  const v = vec4.fromValues(p[0], p[1], p[2], 1);
  vec4.transformMat4(v, v, m);
  return [v[0], v[1], v[2]];
}

function trilinearSample(
  image: vtkImageData,
  ijk: Vec3,
  scalars: Float32Array | Int16Array | Uint16Array | Int32Array | Uint8Array,
): number {
  const [nx, ny, nz] = image.getDimensions() as [number, number, number];
  const [x, y, z] = ijk;
  if (x < 0 || y < 0 || z < 0 || x > nx - 1 || y > ny - 1 || z > nz - 1)
    return NaN;

  const x0 = Math.floor(x),
    y0 = Math.floor(y),
    z0 = Math.floor(z);
  const x1 = Math.min(x0 + 1, nx - 1);
  const y1 = Math.min(y0 + 1, ny - 1);
  const z1 = Math.min(z0 + 1, nz - 1);
  const dx = x - x0,
    dy = y - y0,
    dz = z - z0;

  const strideY = nx,
    strideZ = nx * ny;
  const idx = (ix: number, iy: number, iz: number) =>
    iz * strideZ + iy * strideY + ix;

  const c000 = scalars[idx(x0, y0, z0)],
    c100 = scalars[idx(x1, y0, z0)];
  const c010 = scalars[idx(x0, y1, z0)],
    c110 = scalars[idx(x1, y1, z0)];
  const c001 = scalars[idx(x0, y0, z1)],
    c101 = scalars[idx(x1, y0, z1)];
  const c011 = scalars[idx(x0, y1, z1)],
    c111 = scalars[idx(x1, y1, z1)];

  const c00 = c000 * (1 - dx) + c100 * dx,
    c10 = c010 * (1 - dx) + c110 * dx;
  const c01 = c001 * (1 - dx) + c101 * dx,
    c11 = c011 * (1 - dx) + c111 * dx;
  const c0 = c00 * (1 - dy) + c10 * dy,
    c1 = c01 * (1 - dy) + c11 * dy;
  return c0 * (1 - dz) + c1 * dz;
}

function nearestSampleLabel(
  image: vtkImageData,
  ijk: Vec3,
  scalars: Uint8Array,
): number {
  const [nx, ny, nz] = image.getDimensions() as [number, number, number];
  const x = Math.round(ijk[0]);
  const y = Math.round(ijk[1]);
  const z = Math.round(ijk[2]);
  if (x < 0 || y < 0 || z < 0 || x >= nx || y >= ny || z >= nz) {
    return 0;
  }

  return scalars[z * nx * ny + y * nx + x] ?? 0;
}

function reduceLabelProjection(values: number[]): number {
  let maxValue = 0;
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] > maxValue) {
      maxValue = values[i];
    }
  }
  return maxValue;
}

function reduceProjection(values: number[], mode: ProjectionMode): number {
  if (values.length === 0) return NaN;
  switch (mode) {
    case ProjectionMode.MAX:
      return values.reduce((a, b) => (a > b ? a : b), -Infinity);
    case ProjectionMode.MIN:
      return values.reduce((a, b) => (a < b ? a : b), +Infinity);
    case ProjectionMode.AVERAGE:
    default:
      return values.reduce((a, b) => a + b, 0) / values.length;
  }
}

export function exportCPRHU(
  mapper: vtkImageCPRMapper,
  imageData: vtkImageData,
  opt: CPRExportOptions = {},
): CPRExportResult {
  if (!mapper.preRenderCheck()) {
    throw new Error(
      'ImageCPRMapper is not ready (image/centerline/orientation).',
    );
  }

  // -------- 출력 해상도 --------
  const widthMM = mapper.getWidth();
  const heightMM = mapper.getHeight();
  const spacingU = opt.pixelSpacingU ?? 0.5;
  const spacingV = opt.pixelSpacingV ?? 0.5;
  const outW = opt.outWidthPx ?? Math.max(1, Math.round(widthMM / spacingU));
  const outH = opt.outHeightPx ?? Math.max(1, Math.round(heightMM / spacingV));

  // 출력 버퍼(Int16 유지)
  const scalars = imageData.getPointData().getScalars().getData() as Int16Array;
  let fillMin = +Infinity;
  for (let i = 0; i < scalars.length; i++)
    fillMin = Math.min(fillMin, scalars[i]);
  const out = new Int16Array(outW * outH).fill(fillMin);

  // -------- 볼륨 좌표계 --------
  const worldToIndex = imageData.getWorldToIndex() as mat4;

  // -------- 슬랩 설정 --------
  const projectionEnabled = mapper.isProjectionEnabled();
  const nSamples = mapper.getProjectionSlabNumberOfSamples();
  const slabThickness = mapper.getProjectionSlabThickness();
  const projMode = mapper.getProjectionMode();
  const slabAxisSel = opt.projectionAxis ?? 'normal';

  // -------- 행렬/보조 --------
  const Mdir: mat3 =
    ((mapper as any).getDirectionMatrix?.() as mat3) || mat3.create(); // rotateView가 넣은 기준행렬

  const mul3 = (A: mat3, v: Vec3): Vec3 => {
    const x = A[0] * v[0] + A[3] * v[1] + A[6] * v[2];
    const y = A[1] * v[0] + A[4] * v[1] + A[7] * v[2];
    const z = A[2] * v[0] + A[5] * v[1] + A[8] * v[2];
    const l = Math.hypot(x, y, z) || 1;
    return [x / l, y / l, z / l];
  };

  const quatToM3 = (q: quat) => {
    const m = mat3.create();
    mat3.fromQuat(m, q);
    return m;
  };

  // 모델 공간 기본축
  const T0: Vec3 = [1, 0, 0]; // tangent
  const B0: Vec3 = [0, 1, 0]; // bitangent
  const N0: Vec3 = [0, 0, 1]; // normal

  // 샘플링 간격
  const du = widthMM / outW;

  const cp = mapper.getCenterPoint(); // stretched면 존재

  let p = 0,
    minHU = +Infinity,
    maxHU = -Infinity,
    nanCount = 0;

  for (let v = 0; v < outH; v++) {
    // centerline 거리(mm) → VTK와 동일한 (v+0.5)/outH 스킴
    const distance = ((v + 0.5) / outH) * heightMM;
    const posOri = mapper.getCenterlinePositionAndOrientation(distance);
    if (!posOri.position || !posOri.orientation) {
      for (let u = 0; u < outW; u++, p++) nanCount++;
      continue;
    }

    const P = posOri.position as Vec3;
    const q = posOri.orientation as quat;
    const Mq = quatToM3(q);

    // 최종 월드축 = Mq * Mdir * axis0  (rotateView의 규칙을 그대로 복제)
    const Tloc = mul3(Mdir, T0);
    const Bloc = mul3(Mdir, B0);
    const Nloc = mul3(Mdir, N0);

    const Tw = mul3(Mq, Tloc);
    const Bw = mul3(Mq, Bloc);
    const Nw = mul3(Mq, Nloc);

    // U(가로)축: Tangent 사용 (VTK와 0도/임의각 모두 일치)
    const D: Vec3 = Tw;

    // 슬랩축 선택
    let slabAxis: Vec3;
    if (slabAxisSel === 'normal') slabAxis = Nw;
    else if (slabAxisSel === 'bitangent') slabAxis = Bw;
    else slabAxis = D;

    // stretched 정렬: centerPoint와 같은 U선상으로만 맞춤
    let Pcenter: Vec3 = P;
    if (cp) {
      const toCP: Vec3 = [cp[0] - P[0], cp[1] - P[1], cp[2] - P[2]];
      const offU = toCP[0] * D[0] + toCP[1] * D[1] + toCP[2] * D[2];
      Pcenter = [P[0] + offU * D[0], P[1] + offU * D[1], P[2] + offU * D[2]];
    }

    for (let u = 0; u < outW; u++, p++) {
      const xMM = (u + 0.5) * du - widthMM * 0.5;
      const Pw: Vec3 = [
        Pcenter[0] + xMM * D[0],
        Pcenter[1] + xMM * D[1],
        Pcenter[2] + xMM * D[2],
      ];

      let values: number[] = [];
      if (projectionEnabled && nSamples > 1 && slabThickness > 0) {
        const half = slabThickness * 0.5;
        const step = slabThickness / (nSamples - 1);
        for (let i = 0; i < nSamples; i++) {
          const t = -half + i * step;
          const PwT: Vec3 = [
            Pw[0] + t * slabAxis[0],
            Pw[1] + t * slabAxis[1],
            Pw[2] + t * slabAxis[2],
          ];
          const ijkT = applyMat4Point(worldToIndex, PwT);
          values.push(trilinearSample(imageData, ijkT, scalars));
        }
      } else {
        const ijk = applyMat4Point(worldToIndex, Pw);
        values = [trilinearSample(imageData, ijk, scalars)];
      }

      const hu = reduceProjection(values, projMode);
      if (Number.isNaN(hu)) {
        nanCount++;
        out[p] = fillMin;
      } else {
        if (hu < minHU) minHU = hu;
        if (hu > maxHU) maxHU = hu;
        out[p] = hu;
      }
    }
  }

  return {
    width: outW,
    height: outH,
    spacingU: widthMM / outW,
    spacingV: heightMM / outH,
    data: out,
    extent: {
      minHU: Number.isFinite(minHU) ? minHU : NaN,
      maxHU: Number.isFinite(maxHU) ? maxHU : NaN,
      nanCount,
    },
    angle: opt.angle ?? 0,
  };
}

export function exportCPRHUForMode(
  mapper: vtkImageCPRMapper,
  imageData: vtkImageData,
  mode: string,
  opt: CPRExportOptions = {},
) {
  // 모드에 맞춰 projection 축/spacing 기본값만 살짝 튜닝
  const defaultOpt: CPRExportOptions =
    mode === 'straightened'
      ? { pixelSpacingU: 0.4, pixelSpacingV: 0.4, projectionAxis: 'normal' }
      : { pixelSpacingU: 0.4, pixelSpacingV: 0.4, projectionAxis: 'bitangent' };

  return exportCPRHU(mapper, imageData, { ...defaultOpt, ...opt });
}

export function exportCPRLabelMap(
  mapper: vtkImageCPRMapper,
  imageData: vtkImageData,
  opt: {
    outWidthPx?: number;
    outHeightPx?: number;
    useCenterlineRows?: boolean;
    projectionAxis?: 'normal' | 'bitangent' | 'tangent';
    maxSlabSamples?: number;
  } = {},
): CPRLabelExportResult {
  if (!mapper.preRenderCheck()) {
    throw new Error(
      'ImageCPRMapper is not ready (image/centerline/orientation).',
    );
  }

  const widthMM = mapper.getWidth();
  const heightMM = mapper.getHeight();
  const scalars = imageData.getPointData().getScalars().getData() as Uint8Array;
  const worldToIndex = imageData.getWorldToIndex() as mat4;
  const projectionEnabled = mapper.isProjectionEnabled();
  const nSamples = mapper.getProjectionSlabNumberOfSamples();
  const slabThickness = mapper.getProjectionSlabThickness();
  const slabAxisSel = opt.projectionAxis ?? 'normal';
  const maxSlabSamples = Math.max(1, Math.floor(opt.maxSlabSamples ?? 8));
  const sampleCount =
    projectionEnabled && nSamples > 1 && slabThickness > 0
      ? Math.min(maxSlabSamples, Math.max(2, nSamples))
      : 1;
  const orientedCenterline =
    (mapper as any).getOrientedCenterline?.() ??
    (mapper as any).getCenterlineData?.();
  const distances = orientedCenterline?.getDistancesToFirstPoint?.() as
    | number[]
    | undefined;
  const useCenterlineRows =
    opt.useCenterlineRows !== false &&
    Array.isArray(distances) &&
    distances.length > 1;
  const outH = useCenterlineRows
    ? distances.length
    : (opt.outHeightPx ?? Math.max(1, Math.round(heightMM / 0.5)));
  const outW =
    opt.outWidthPx ?? Math.max(64, Math.min(768, Math.round(widthMM / 0.25)));
  const out = new Uint8Array(outW * outH);
  const rows: Array<{
    center: Vec3;
    direction: Vec3;
    slabAxis: Vec3;
  }> = new Array(outH);

  const Mdir: mat3 =
    ((mapper as any).getDirectionMatrix?.() as mat3) || mat3.create();
  const mul3 = (A: mat3, v: Vec3): Vec3 => {
    const x = A[0] * v[0] + A[3] * v[1] + A[6] * v[2];
    const y = A[1] * v[0] + A[4] * v[1] + A[7] * v[2];
    const z = A[2] * v[0] + A[5] * v[1] + A[8] * v[2];
    const l = Math.hypot(x, y, z) || 1;
    return [x / l, y / l, z / l];
  };
  const quatToM3 = (q: quat) => {
    const m = mat3.create();
    mat3.fromQuat(m, q);
    return m;
  };
  const cp = mapper.getCenterPoint();
  const T0: Vec3 = [1, 0, 0];
  const B0: Vec3 = [0, 1, 0];
  const N0: Vec3 = [0, 0, 1];

  for (let v = 0; v < outH; v += 1) {
    const distance = useCenterlineRows
      ? distances![v]
      : ((v + 0.5) / outH) * heightMM;
    const posOri = mapper.getCenterlinePositionAndOrientation(distance);
    if (!posOri.position || !posOri.orientation) {
      continue;
    }

    const P = posOri.position as Vec3;
    const Mq = quatToM3(posOri.orientation as quat);
    const D = mul3(Mq, mul3(Mdir, T0));
    const Bw = mul3(Mq, mul3(Mdir, B0));
    const Nw = mul3(Mq, mul3(Mdir, N0));

    let Pcenter: Vec3 = P;
    if (cp) {
      const toCP: Vec3 = [cp[0] - P[0], cp[1] - P[1], cp[2] - P[2]];
      const offU = toCP[0] * D[0] + toCP[1] * D[1] + toCP[2] * D[2];
      Pcenter = [P[0] + offU * D[0], P[1] + offU * D[1], P[2] + offU * D[2]];
    }
    let slabAxis: Vec3;
    if (slabAxisSel === 'bitangent') slabAxis = Bw;
    else if (slabAxisSel === 'tangent') slabAxis = D;
    else slabAxis = Nw;
    rows[v] = {
      center: Pcenter,
      direction: D,
      slabAxis,
    };

    for (let u = 0; u < outW; u += 1) {
      const xMM = outW <= 0 ? 0 : ((u + 0.5) / outW - 0.5) * widthMM;
      const worldPoint: Vec3 = [
        Pcenter[0] + xMM * D[0],
        Pcenter[1] + xMM * D[1],
        Pcenter[2] + xMM * D[2],
      ];
      if (sampleCount > 1) {
        const samples: number[] = [];
        const half = slabThickness * 0.5;
        const step = slabThickness / (sampleCount - 1);
        for (let i = 0; i < sampleCount; i += 1) {
          const t = -half + i * step;
          const slabPoint: Vec3 = [
            worldPoint[0] + t * slabAxis[0],
            worldPoint[1] + t * slabAxis[1],
            worldPoint[2] + t * slabAxis[2],
          ];
          const ijk = applyMat4Point(worldToIndex, slabPoint);
          samples.push(nearestSampleLabel(imageData, ijk, scalars));
        }
        out[v * outW + u] = reduceLabelProjection(samples);
      } else {
        const ijk = applyMat4Point(worldToIndex, worldPoint);
        out[v * outW + u] = nearestSampleLabel(imageData, ijk, scalars);
      }
    }
  }

  return {
    width: outW,
    height: outH,
    data: out,
    widthMM,
    heightMM,
    rows,
  };
}

// HU -> [0,255] (WL/WW 윈도우링)
function huToUint8(hu: number, wc = 40, ww = 400): number {
  if (!Number.isFinite(hu)) return 0;
  const y = (hu - (wc - 0.5)) / (ww - 1) + 0.5;
  const v = Math.max(0, Math.min(1, y));
  return Math.round(v * 255);
}

/** exportCPRHU() 결과 -> PNG dataURL */
export function cprToPNGDataURL(
  res: { width: number; height: number; data: Int16Array },
  wc = 40,
  ww = 400,
): string {
  const { width, height, data } = res;
  const rgba = new Uint8ClampedArray(width * height * 4);
  let p = 0;
  for (let i = 0; i < data.length; i++) {
    const g = huToUint8(data[i], wc, ww);
    rgba[p++] = g;
    rgba[p++] = g;
    rgba[p++] = g;
    rgba[p++] = 255;
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.putImageData(new ImageData(rgba, width, height), 0, 0);
  return canvas.toDataURL('image/png'); // ← PNG!
}

export interface StandaloneCPRParams {
  imageData: vtkImageData;
  centerlineData: vtkPolyData;

  mode: 'stretched' | 'straightened';
  widthMM: number;

  axis: vec3;
  savedTranslation: vec3;

  pixelSpacingU?: number;
  pixelSpacingV?: number;
  projectionAxis?: 'normal' | 'bitangent' | 'tangent';

  baseAngleDeg?: number;
  stepAngleDeg: number;
  count: number;
  currentVOI: VOI;
}

export function exportCPRPNGStandaloneMultiAngle(
  params: StandaloneCPRParams,
): CPRExportResult[] {
  const {
    imageData,
    centerlineData,
    mode,
    widthMM,
    axis,
    savedTranslation,
    pixelSpacingU,
    pixelSpacingV,
    projectionAxis = 'normal',
    baseAngleDeg = 0,
    stepAngleDeg,
    count,
    currentVOI,
  } = params;

  if (count <= 0) {
    return [];
  }

  const renderer = vtkRenderer.newInstance();
  const { mapper, actor } = createCPRActorAndMapper({
    data: imageData,
    mode,
    width: widthMM,
    centerline: centerlineData,
    currentVOI,
    isPaintData: false,
    colorPoints: undefined,
    opacityPoints: undefined,
  });

  renderer.addActor(actor as vtkImageSlice);
  const results: CPRExportResult[] = [];

  try {
    for (let i = 0; i < count; i++) {
      const angleDeg = baseAngleDeg + stepAngleDeg * i;
      const radian = radiansFromDegrees(angleDeg);

      // 각도 바뀔 때마다 rotateView 적용 (= updateCPRAngleAndPath2 역할)
      rotateView({
        radian,
        mapper: mapper as vtkImageCPRMapper,
        actor: actor as vtkImageSlice,
        renderer,
        savedTranslation,
        axis,
        mode,
      });

      const huRes = exportCPRHU(mapper as vtkImageCPRMapper, imageData, {
        pixelSpacingU,
        pixelSpacingV,
        projectionAxis,
        rotationAxisWorld: axis,
      });

      results.push(huRes);
    }
  } finally {
    try {
      renderer.removeActor(actor as vtkImageSlice);
    } catch {}
    try {
      (actor as vtkImageSlice).delete?.();
    } catch {}
    try {
      (mapper as vtkImageCPRMapper).delete?.();
    } catch {}
    try {
      renderer.delete?.();
    } catch {}
  }

  return results;
}

// ===============================
//  Standalone CPR HU + Label 캡쳐
// ===============================

export interface StandaloneCPRLabelParams {
  imageData: vtkImageData;
  labelData: vtkImageData;
  centerlineData: vtkPolyData;

  mode: 'stretched' | 'straightened';
  widthMM: number;

  // updateCPRAngleAndPath2 에서 쓰던 값 그대로 외부에서 넘겨줌
  axis: vec3;
  savedTranslation: vec3; // [dx, dy, dz] (dx, dy 사용)

  // label용 color/opacity
  colorPoints?: Array<[number, number, number, number]>;
  opacityPoints?: Array<[number, number]>;

  // 캡쳐 해상도 (렌더윈도우 캔버스 크기)
  captureWidth: number;
  captureHeight: number;

  baseAngleDeg?: number; // 시작 각도 (기본 0)
  stepAngleDeg: number; // 각 스텝마다 증가시킬 각도
  count: number; // 몇 장 찍을지
  currentVOI: VOI;
}

export interface StandaloneCPRLabelMultiAngleResult {
  urls: string[];
  maxwidth: number;
  maxheight: number;
}

export async function exportCPRPNGStandaloneMultiAngleWithLabel(
  params: StandaloneCPRLabelParams,
): Promise<StandaloneCPRLabelMultiAngleResult> {
  const {
    imageData,
    labelData,
    centerlineData,
    mode,
    widthMM,
    axis,
    savedTranslation,
    colorPoints,
    opacityPoints,
    captureWidth,
    captureHeight,
    baseAngleDeg = 0,
    stepAngleDeg,
    count,
    currentVOI,
  } = params;

  if (count <= 0) {
    return { urls: [], maxwidth: 0, maxheight: 0 };
  }

  // 1) offscreen genericRenderWindow / renderer 생성
  const genericRenderWindow = vtkGenericRenderWindow.newInstance();
  setNewContainer(genericRenderWindow, captureWidth, captureHeight);

  const renderer = genericRenderWindow.getRenderer();
  const renderWindow = genericRenderWindow.getRenderWindow();

  genericRenderWindow.setBackground([0, 0, 0]);

  // 2) HU용 CPR actor / mapper
  const { mapper: cprMapper, actor: cprActor } = createCPRActorAndMapper({
    data: imageData,
    mode,
    width: widthMM,
    centerline: centerlineData,
    currentVOI,
    isPaintData: false,
  });

  // 3) label용 CPR actor / mapper
  const { mapper: paintMapper, actor: paintActor } = createCPRActorAndMapper({
    data: labelData,
    mode,
    width: widthMM,
    centerline: centerlineData,
    currentVOI,
    isPaintData: true,
    colorPoints,
    opacityPoints,
  });

  renderer.addActor(cprActor);
  renderer.addActor(paintActor);

  renderer.resetCamera();
  renderer.getActiveCamera().setParallelProjection(true);

  const urls: string[] = [];
  let maxwidth = 0;
  let maxheight = 0;

  try {
    for (let i = 0; i < count; i++) {
      const angleDeg = baseAngleDeg + stepAngleDeg * i;
      const rad = radiansFromDegrees(angleDeg);

      // HU actor 회전/보정
      rotateView({
        radian: rad,
        mapper: cprMapper as vtkImageCPRMapper,
        actor: cprActor as vtkImageSlice,
        renderer,
        savedTranslation,
        axis,
        mode,
      });

      // label actor 회전/보정
      rotateView({
        radian: rad,
        mapper: paintMapper as vtkImageCPRMapper,
        actor: paintActor as vtkImageSlice,
        renderer,
        savedTranslation,
        axis,
        mode,
      });

      renderer.resetCameraClippingRange();
      renderWindow.render();
      let url = await captureView({
        type: 'vtk',
        renderWindow: renderWindow,
      });
      const [u, w, h] = await trimMarginsToContent(url);
      url = u;
      urls.push(url);
      maxwidth = Math.max(maxwidth, w);
      maxheight = Math.max(maxheight, h);
    }
  } finally {
    try {
      renderer.removeActor(cprActor);
      renderer.removeActor(paintActor);
    } catch {}
    try {
      (cprActor as vtkImageSlice).delete?.();
    } catch {}
    try {
      (paintActor as vtkImageSlice).delete?.();
    } catch {}
    try {
      (cprMapper as vtkImageCPRMapper).delete?.();
    } catch {}
    try {
      (paintMapper as vtkImageCPRMapper).delete?.();
    } catch {}
    try {
      genericRenderWindow.delete?.();
    } catch {}
  }

  return { urls, maxwidth, maxheight };
}
